import type { RouteRecordRaw, Router } from 'vue-router'
import type { MenuNode } from '@/types/api'

/**
 * 菜单树 → 动态路由（附录C §2）：组件按菜单 path 约定懒加载，找不到时回落到占位页，
 * 由占位页显式提示「页面未实现」，而不是静默 404。
 */
const adminViews = import.meta.glob('/src/views/admin/**/*.vue')

function resolveView(path: string): RouteRecordRaw['component'] {
  const key = `/src/views/admin/${path.replace(/^\/+/, '')}.vue`
  const matched = adminViews[key] as (() => Promise<unknown>) | undefined
  return (matched ?? (() => import('@/views/admin/PlaceholderView.vue'))) as RouteRecordRaw['component']
}

export function menuToRoutes(nodes: MenuNode[]): RouteRecordRaw[] {
  return nodes.flatMap((node) => {
    // 没有 path 的节点不是导航入口：子节点按「相对 /admin 的完整路径」上提
    if (!node.path) {
      return node.children?.length ? menuToRoutes(node.children) : []
    }
    const path = node.path.replace(/^\/+/, '')
    const children = node.children?.length ? menuToRoutes(node.children) : undefined
    return [{
      path,
      name: node.name || path,
      component: resolveView(path),
      meta: { title: node.title, icon: node.icon ?? null, permCode: node.permCode ?? null },
      ...(children ? { children } : {}),
    } as RouteRecordRaw]
  })
}

/** 已动态注册的菜单路由名，切换系统时先移除，避免残留上一系统的路由。 */
let dynamicRouteNames: string[] = []

/**
 * 把菜单树注册到名为 `admin` 的父路由下（重复调用会先清理上一批）。
 *
 * 管理后台页面已按固定路径静态注册（便于开发与联调），
 * 因此这里既要按 name 去重，也要按**完整路径**去重，避免同一路径被注册两次。
 */
export function addMenuRoutes(router: Router, nodes: MenuNode[]): void {
  dynamicRouteNames.forEach((name) => router.hasRoute(name) && router.removeRoute(name))
  dynamicRouteNames = []
  const existingPaths = new Set(router.getRoutes().map((route) => route.path))
  for (const route of menuToRoutes(nodes)) {
    const fullPath = `/admin/${String(route.path).replace(/^\/+/, '')}`
    const duplicatedByName = typeof route.name === 'string' && router.hasRoute(route.name)
    if (duplicatedByName || existingPaths.has(fullPath)) {
      continue
    }
    router.addRoute('admin', route)
    if (typeof route.name === 'string') {
      dynamicRouteNames.push(route.name)
    }
  }
}

/** 菜单 `path` 是相对 `/admin` 的完整路由（不是相对父级的片段）。 */
function menuIndex(node: MenuNode): string {
  // 分组节点没有可导航的目标：归到 /admin（等价首页），而不是让整棵菜单渲染抛错
  if (!node.path) {
    return '/admin'
  }
  return `/admin/${node.path.replace(/^\/+/, '')}`
}

/** 菜单树的所有路径（含子级），供路由守卫做「越权兜底」（不在菜单树中的路径按 403 处理）。 */
export function flattenMenuPaths(nodes: MenuNode[]): string[] {
  return nodes.flatMap((node) => {
    const nested = node.children?.length ? flattenMenuPaths(node.children) : []
    // 分组节点（path 为空）只贡献子级路径：把容器算成 /admin 会让
    // "target.startsWith('/admin/')" 命中所有管理页，越权兜底整条失效
    return node.path ? [menuIndex(node), ...nested] : nested
  })
}

/** 菜单树 → el-menu 的层级数据（AdminLayout 渲染用）。 */
export interface MenuItem {
  index: string
  title: string
  icon?: string | null
  children?: MenuItem[]
}

export function toMenuItems(nodes: MenuNode[]): MenuItem[] {
  return [...nodes]
    // 没有 path 又没有子节点的菜单点不进去，渲染出来只是一个死入口
    .filter((node) => Boolean(node.path) || (node.children?.length ?? 0) > 0)
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    .map((node) => {
      const index = menuIndex(node)
      const nested = node.children?.length ? toMenuItems(node.children) : []
      // 子节点被过滤干净时不留空 children：否则上层会误以为它是个分组
      const children = nested.length > 0 ? nested : undefined
      return { index, title: node.title, icon: node.icon ?? null, ...(children ? { children } : {}) }
    })
}
